import { useState } from 'react';
import {
  ToggleLeft,
  SlidersHorizontal,
  Search,
  Beaker,
  ShieldAlert,
  Pencil,
  Trash2,
  GripVertical,
  Weight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type {
  Criterion,
  CriterionType,
  BooleanConfig,
  ScaleConfig,
  TextMatchConfig,
  PICOConfig,
  BiasRiskConfig,
} from '@/features/criteria/types';
import {
  CRITERION_TYPE_LABELS,
  isBooleanConfig,
  isScaleConfig,
  isTextMatchConfig,
  isPICOConfig,
  isBiasRiskConfig,
} from '@/features/criteria/types';

// =========================================
// Props
// =========================================

interface CriterionCardProps {
  criterion: Criterion;
  onEdit?: (criterion: Criterion) => void;
  onDelete?: (criterionId: string) => void;
  isDeleting?: boolean;
}

// =========================================
// Constants
// =========================================

const TYPE_ICONS: Record<CriterionType, typeof ToggleLeft> = {
  boolean: ToggleLeft,
  scale: SlidersHorizontal,
  text_match: Search,
  pico: Beaker,
  bias_risk: ShieldAlert,
};

const TYPE_COLORS: Record<CriterionType, string> = {
  boolean: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  scale: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  text_match: 'bg-amber-50 text-amber-700 border-amber-200',
  pico: 'bg-blue-50 text-blue-700 border-blue-200',
  bias_risk: 'bg-rose-50 text-rose-700 border-rose-200',
};

const TYPE_ICON_BG: Record<CriterionType, string> = {
  boolean: 'bg-indigo-100 text-indigo-600',
  scale: 'bg-emerald-100 text-emerald-600',
  text_match: 'bg-amber-100 text-amber-600',
  pico: 'bg-blue-100 text-blue-600',
  bias_risk: 'bg-rose-100 text-rose-600',
};

const WEIGHT_COLORS: Record<number, string> = {
  1: 'bg-slate-200',
  2: 'bg-slate-300',
  3: 'bg-amber-400',
  4: 'bg-orange-500',
  5: 'bg-rose-500',
};

const WEIGHT_LABELS: Record<number, string> = {
  1: 'Very Low',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Critical',
};

// =========================================
// Config summary renderers
// =========================================

function BooleanSummary({ config }: { config: BooleanConfig }) {
  return (
    <p className="text-xs text-muted-foreground line-clamp-2">
      Q: {config.question || 'No question set'}
    </p>
  );
}

function ScaleSummary({ config }: { config: ScaleConfig }) {
  return (
    <p className="text-xs text-muted-foreground">
      Range: {config.min}–{config.max} (step {config.step})
      {Object.keys(config.labels).length > 0 && (
        <span className="ml-1">
          · {Object.keys(config.labels).length} labels
        </span>
      )}
    </p>
  );
}

function TextMatchSummary({ config }: { config: TextMatchConfig }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">
        {config.keywords.length} keyword{config.keywords.length !== 1 ? 's' : ''} · Match {config.mode}
        {config.case_sensitive ? ' · Case sensitive' : ''}
      </p>
      {config.keywords.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {config.keywords.slice(0, 5).map((kw) => (
            <Badge key={kw} variant="secondary" className="text-[10px] px-1.5 py-0">
              {kw}
            </Badge>
          ))}
          {config.keywords.length > 5 && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              +{config.keywords.length - 5}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

function PICOSummary({ config }: { config: PICOConfig }) {
  const fields = [
    { label: 'P', value: config.population },
    { label: 'I', value: config.intervention },
    { label: 'C', value: config.comparator },
    { label: 'O', value: config.outcome },
  ].filter((f) => f.value);

  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
      {fields.map(({ label, value }) => (
        <p key={label} className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{label}:</span>{' '}
          <span className="line-clamp-1">{value}</span>
        </p>
      ))}
      {fields.length === 0 && (
        <p className="text-xs text-muted-foreground">No PICO fields set</p>
      )}
    </div>
  );
}

function BiasRiskSummary({ config }: { config: BiasRiskConfig }) {
  return (
    <p className="text-xs text-muted-foreground">
      {config.checklist.length} checklist item{config.checklist.length !== 1 ? 's' : ''} · Scale: {config.scale.join(', ').replace(/_/g, ' ')}
    </p>
  );
}

// =========================================
// Component
// =========================================

export function CriterionCard({
  criterion,
  onEdit,
  onDelete,
  isDeleting,
}: CriterionCardProps) {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const Icon = TYPE_ICONS[criterion.type];

  return (
    <>
      <Card className="group relative transition-shadow hover:shadow-md">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Drag handle area */}
            <div className="flex flex-col items-center gap-1 pt-0.5">
              <GripVertical className="h-4 w-4 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
              <div className={cn('rounded-lg p-2', TYPE_ICON_BG[criterion.type])}>
                <Icon className="h-4 w-4" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-2">
              {/* Header */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-medium text-sm text-foreground truncate">
                    {criterion.name}
                  </h3>
                  {criterion.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                      {criterion.description}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  {onEdit && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => onEdit(criterion)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit</TooltipContent>
                    </Tooltip>
                  )}
                  {onDelete && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => setConfirmDeleteOpen(true)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>

              {/* Badges row */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={cn('text-[10px]', TYPE_COLORS[criterion.type])}>
                  {CRITERION_TYPE_LABELS[criterion.type]}
                </Badge>
                {criterion.category && (
                  <Badge variant="secondary" className="text-[10px]">
                    {criterion.category}
                  </Badge>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1">
                      <Weight className="h-3 w-3 text-muted-foreground" />
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }, (_, i) => (
                          <div
                            key={i}
                            className={cn(
                              'h-1.5 w-3 rounded-full',
                              i < criterion.weight
                                ? WEIGHT_COLORS[criterion.weight]
                                : 'bg-muted'
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Weight: {criterion.weight}/5 ({WEIGHT_LABELS[criterion.weight]})
                  </TooltipContent>
                </Tooltip>
              </div>

              {/* Config summary */}
              <div className="pt-1 border-t border-border/50">
                {isBooleanConfig(criterion.config) && (
                  <BooleanSummary config={criterion.config} />
                )}
                {isScaleConfig(criterion.config) && (
                  <ScaleSummary config={criterion.config} />
                )}
                {isTextMatchConfig(criterion.config) && (
                  <TextMatchSummary config={criterion.config} />
                )}
                {isPICOConfig(criterion.config) && (
                  <PICOSummary config={criterion.config} />
                )}
                {isBiasRiskConfig(criterion.config) && (
                  <BiasRiskSummary config={criterion.config} />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Criterion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{criterion.name}&rdquo;? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isDeleting}
              onClick={() => {
                onDelete?.(criterion.id);
                setConfirmDeleteOpen(false);
              }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
